import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';
import { GettingStartedDialog } from './GettingStartedDialog';
import { PageHeader } from './PageHeader';
import { SearchLauncher } from './SearchLauncher';
import { SpecsLogo } from './SpecsLogo';
import { ThemeToggle } from './ThemeToggle';
import { Splash } from './Splash';
import { UserMenu } from './UserMenu';
import { useAuth } from '../hooks/useAuth';
import { APP_TITLE } from '../config/branding';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { isAuthenticated, isLoading, user, login, logout } = useAuth();
  const [helpOpen, setHelpOpen] = useState(false);

  // Auth still resolving (e.g. the OAuth code→token exchange on redirect): show a
  // lightweight, programmatically-announced loading indicator instead of briefly
  // flashing the signed-out Splash before the session lands.
  if (isLoading) {
    return (
      <div
        data-testid="layout-loading"
        role="status"
        aria-busy="true"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--genai-sem-page-bg)',
          color: 'var(--genai-sem-text-primary)',
        }}
      >
        Signing you in…
      </div>
    );
  }

  // Signed out: a full-page branded splash replaces the header + app chrome.
  if (!isAuthenticated) {
    return <Splash onSignIn={login} />;
  }

  return (
    <div
      style={{ minHeight: '100vh', backgroundColor: 'var(--genai-sem-page-bg)', color: 'var(--genai-sem-text-primary)' }}
    >
      {/* Header */}
      <header style={{
        position: 'relative',
        // Exploration A — Cox-blue diagonal gradient container (light + dark via the
        // gradient token). Only the container is restyled here; logo, search and
        // buttons keep their existing per-mode styles.
        background: 'var(--genai-sem-header-bg-gradient)',
        borderTop: '2px solid var(--genai-sem-accent-border)',
        padding: 0,
      }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--genai-space-3xl)', height: '56px' }}>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
            <Link to="/" className="specs-logo" aria-label={`Specs-Ray: ${APP_TITLE} — home`}>
              <SpecsLogo />
              <span className="sr-only">Specs-Ray: {APP_TITLE}</span>
            </Link>
          </h1>
          {/* Scope-independent global search (⌘K jump). Centered in the header,
              stretching to fill the space between the title and the right actions.
              Scan is a team-scoped tab in the page header, not a global-bar action.
              Its field self-applies the dark theme (it sits on the dark gradient);
              the results dropdown deliberately keeps the page theme — see SearchLauncher. */}
          <SearchLauncher />
          {/* Exploration A: the header gradient is dark in both modes, so the right
              actions borrow the dark-mode token values regardless of page theme. The
              logo (child #1, above) is intentionally excluded so it still follows the
              page theme. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }} data-theme="dark">
            <ThemeToggle />
            <Button
              variant="secondary"
              size="S"
              leftIcon={<QuestionMarkCircleIcon aria-hidden="true" />}
              aria-label="Getting Started Guide"
              title="Getting Started Guide"
              onClick={() => setHelpOpen(true)}
            />
            <UserMenu email={user?.email} onLogout={logout} />
          </div>
        </div>
      </header>

      {/* Content. Navigation lives in the node-scoped page header (breadcrumb →
          title → tabs); the URL is the single source of truth — no cascade bar. */}
      <main className="container" style={{ padding: '28px 24px' }}>
        <PageHeader />
        {children}
      </main>

      <GettingStartedDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
